import React, {memo} from "react";
import {Link} from "@inertiajs/react";
import {PiArrowUpBold, PiCaretDownBold, PiCaretUpBold, PiCaretUpDownBold, PiStar, PiStarFill, PiWarningBold} from "react-icons/pi";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import HelpTooltip from "../HelpTooltip.jsx";
import Farbfeld from "./Farbfeld.jsx";
import AuslaufendAbzeichen from "./AuslaufendAbzeichen.jsx";
import {ZellMitTooltip, ZellTooltipProvider} from "./ZellTooltip.jsx";
import KwZeitleiste from "./KwZeitleiste.jsx";
import {TOOLTIP_POPPER_CONFIG} from "./tooltipConfig.js";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {COSMOS_PLANUNG_HINWEIS} from "../Stock/bestandTexte.js";
import {formatFarbnummer, formatKw, formatMenge, herkunftText, kwZuMontag, heutigeKw, PRODUKTLINIE_LABEL} from "./format.js";

// Feste vordere Spalten (Auftrag AP3 B) - Breiten in px, sticky beim
// waagerechten Scrollen der Wochenspalten. Die Tabelle selbst bekommt
// table-layout: fixed über ein <colgroup> (siehe unten) - damit bestimmen
// AUSSCHLIESSLICH die <col>-Breiten die Spaltenbreiten, unabhängig davon,
// welche Kopfzeile zuerst im Markup steht (Zeitleiste + Hauptkopf haben
// unterschiedlich viele/breite Zellen je Zeile). Die hier fest vergebenen
// "left"-Offsets der sticky-Spalten setzen genau diese Breiten voraus -
// das war die Ursache der Übereinanderlagen beim Scrollen (Sichtprüfung 1,
// Befund 10) und musste bei jeder Verbreiterung (Rückmeldung T5) mitziehen.
const FIXE_SPALTEN = [
    {key: "ident", label: "Farbton", width: 245, hilfe: "Farbnummer und Bezeichnung des Farbtons.", sortierbar: true},
    {key: "artikelnummer", label: "Artikelnr.", width: 100, hilfe: "Interne Artikelnummer, verlinkt zum Artikel-Blatt.", sortierbar: true},
    {key: "bestand", label: "Lagerbestand", width: 110, hilfe: "Zuletzt gemeldeter Lagerbestand (physisch) am Standort, ohne Abzug von „offen, nicht reserviert“.", sortierbar: true},
    {key: "offen", label: "Offen, nicht reserviert (Planung)", width: 150, hilfe: `Menge aus Kundenaufträgen, die das Lager noch nicht verlassen hat (enge Definition: in Erfassung oder bestätigt, noch nicht versandt). ${COSMOS_PLANUNG_HINWEIS}`, sortierbar: true},
    {key: "ausfallwoche", label: "Ausfallwoche", width: 120, hilfe: "Kalenderwoche, in der der Bestand im gewählten Szenario voraussichtlich auf null fällt.", sortierbar: true},
    {key: "reichweite", label: "Reichweite", width: 105, hilfe: "Tage, bis der Bestand im gewählten Szenario auf null fällt.", sortierbar: true},
];

function spaltenLinks() {
    let summe = 0;
    return FIXE_SPALTEN.map((spalte) => {
        const links = summe;
        summe += spalte.width;
        return links;
    });
}

const LINKS = spaltenLinks();
// Rückmeldung T5+T7: die KW-Spalten waren "viel zu dicht beieinander" und
// die Beschriftung nicht vollständig sichtbar - deutlich breiter, mit
// zweizeiliger Beschriftung (KW-Nummer, Datum darunter).
const WOCHE_BREITE = 108;

/**
 * Farbabstufung des prognostizierten Wochenbestands (Auftrag AP3 B): rot
 * bei <= 0, orange bei <= Mindestbestand, sonst grüne Abstufung, kräftiger
 * je weiter über dem Mindestbestand (bezogen auf den Zielbestand) - fester
 * Grünton (#2f9e44, wie TREND_UP_COLOR im übrigen Haus), nur die
 * Deckkraft ändert sich.
 *
 * Befund N7 (Rückmeldung 22.09.2026): Auslaufende Farbtöne bekommen KEINE
 * Alarmfarbe mehr - ihr Bestand läuft absichtlich auf 0 (kein Nachschub
 * geplant, App\Services\Cosmos\Prognose::markenAuslaufend), das ist kein
 * Fehlbestand. Derselbe neutrale Grauton wie bei fehlendem Wert oben
 * (dieselbe Logik wie das Abzeichen "läuft aus") statt Rot/Orange/Grün -
 * die Zahl bleibt sichtbar (0 ist ein echter, gerechneter Wert), nur ohne
 * Alarmierung.
 */
function zellFarbe(wert, mindestbestand, zielbestand, auslaufend = false) {
    if (auslaufend) {
        // Derselbe Ton wie die übrige Zeile, damit die Zeile durchgehend
        // hinterlegt wirkt (Rückmeldung Thorsten 23.09.2026) - ruhig und ohne
        // Ampelfarbe, weil für diese Farbtöne nichts mehr zu entscheiden ist.
        return {backgroundColor: "#e2e6ea", color: "#868e96"};
    }
    if (wert === null || wert === undefined) {
        return {backgroundColor: "#f1f3f5", color: "#868e96"};
    }
    if (wert <= 0) {
        return {backgroundColor: "#f8d7da", color: "#842029", fontWeight: 600};
    }
    if (mindestbestand !== null && mindestbestand !== undefined && wert <= mindestbestand) {
        return {backgroundColor: "#ffe8cc", color: "#7c4a03"};
    }
    const basis = zielbestand && zielbestand > (mindestbestand || 0) ? zielbestand : (mindestbestand || wert) * 2 || wert;
    const spanne = Math.max(1, basis - (mindestbestand || 0));
    const ratio = Math.min(1, Math.max(0, (wert - (mindestbestand || 0)) / spanne));
    const alpha = 0.16 + ratio * 0.34;
    return {backgroundColor: `rgba(47,158,68,${alpha.toFixed(2)})`, color: "#1b4d24"};
}

function IdentitaetName({farbnummer, farbnummerUnter, farbname, auslaufend}) {
    const nummer = farbnummer !== null && farbnummer !== undefined ? `${formatFarbnummer(farbnummer, farbnummerUnter)} ` : "";
    const text = `${nummer}${farbname || "–"}`;

    if (!auslaufend) {
        return (
            <ZellMitTooltip text={text} className="text-truncate d-inline-block" style={{maxWidth: 165}}>
                {text}
            </ZellMitTooltip>
        );
    }

    // Rückmeldung Thorsten 23.09.2026: Das Abzeichen darf in seiner Position
    // nicht springen. Es sitzt deshalb fest oben rechts in der Zelle (dort
    // position:absolute, siehe LieferfaehigkeitZeile) und nicht mehr im
    // Textfluss - der Name bleibt einzeilig wie bei jedem anderen Farbton,
    // die Zeilenhöhe damit über die ganze Tabelle gleich.
    return (
        <ZellMitTooltip text={text} className="text-truncate d-inline-block" style={{maxWidth: 140}}>
            {text}
        </ZellMitTooltip>
    );
}

/**
 * Stern-Schalter "Top-Farbton" (T8: "wir möchten auch selbst manuell einen
 * Farbton als wichtiger als die anderen einstufen können") - nur mit Recht
 * cosmos.parameters bedienbar, sonst reine Anzeige. `topFarbton` kommt vom
 * Server (Rückfall auf false, solange das Feld dort noch fehlt); ein Klick
 * meldet sich beim Aufrufer, der die tatsächliche Speicherung übernimmt.
 */
function TopFarbtonSchalter({aktiv, kannBearbeiten, onToggle, artikelNummer}) {
    const hilfe = kannBearbeiten
        ? aktiv
            ? "Als Top-Farbton markiert - beeinflusst die Sortierung „Wichtigste Farbtöne“. Klick entfernt die Markierung."
            : "Markiert diesen Farbton von Hand als besonders wichtig - beeinflusst die Sortierung „Wichtigste Farbtöne“."
        : aktiv
            ? "Von Hand als Top-Farbton markiert."
            : "Kein Top-Farbton.";

    return (
        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`top-farbton-${artikelNummer}`}>{hilfe}</Tooltip>}>
            <span>
                <button
                    type="button"
                    className="btn btn-sm p-0 border-0 bg-transparent d-inline-flex align-items-center"
                    style={{color: aktiv ? "#f08c00" : "#adb5bd", cursor: kannBearbeiten ? "pointer" : "default"}}
                    disabled={!kannBearbeiten}
                    aria-pressed={aktiv}
                    aria-label={aktiv ? "Top-Farbton, anklicken zum Entfernen" : "Als Top-Farbton markieren"}
                    onClick={kannBearbeiten ? () => onToggle(artikelNummer, !aktiv) : undefined}
                >
                    {aktiv ? <PiStarFill size={15} /> : <PiStar size={15} />}
                </button>
            </span>
        </OverlayTrigger>
    );
}

const LieferfaehigkeitZeile = memo(function LieferfaehigkeitZeile({zeile, horizontWochen, canArtikel, canParameters, onToggleTop}) {
    const nichtGerechnet = zeile.status !== "gerechnet";
    const auslaufend = Boolean(zeile.auslaufend);
    const gedaempft = nichtGerechnet || auslaufend;
    // Auslaufende Farbtöne sollen sich auf einen Blick von den übrigen Zeilen
    // abheben (Rückmeldung Thorsten 23.09.2026: „hinterlege die gesamte Zeile
    // leicht, damit man den Unterschied deutlich erkennt"), bleiben dabei aber
    // ruhig - kein Warnton. Nicht gerechnete Zeilen behalten ihr helleres Grau.
    const zeilenHintergrund = auslaufend ? "#e2e6ea" : "#f8f9fa";
    const warnung = zeile.warnung_zu_spaet?.wert;
    const mindestbestand = zeile.mindestbestand?.wert ?? null;
    const zielbestand = zeile.zielbestand?.wert ?? null;

    return (
        <tr className={gedaempft ? "text-muted" : undefined} style={gedaempft ? {backgroundColor: zeilenHintergrund} : undefined}>
            <td style={{position: "sticky", left: LINKS[0], zIndex: 2, background: gedaempft ? zeilenHintergrund : "#fff", width: FIXE_SPALTEN[0].width, overflow: "hidden"}}>
                <div className="d-flex align-items-center gap-2 flex-wrap" style={{rowGap: 2}}>
                    <TopFarbtonSchalter
                        aktiv={Boolean(zeile.top_farbton)}
                        kannBearbeiten={canParameters}
                        onToggle={onToggleTop}
                        artikelNummer={zeile.artikel_nummer}
                    />
                    <Farbfeld farbwert={zeile.farbwert} />
                    <IdentitaetName farbnummer={zeile.farbnummer} farbnummerUnter={zeile.farbnummer_unter} farbname={zeile.farbname} auslaufend={auslaufend} />
                    {auslaufend && (
                        <span style={{position: "absolute", top: 2, right: 4, zIndex: 1}}>
                            <AuslaufendAbzeichen />
                        </span>
                    )}
                </div>
                {nichtGerechnet && (
                    <ZellMitTooltip text={zeile.grund_nicht_gerechnet} className="badge text-bg-secondary d-inline-block mt-1">
                        nicht gerechnet
                    </ZellMitTooltip>
                )}
            </td>
            <td style={{position: "sticky", left: LINKS[1], zIndex: 2, background: gedaempft ? "#f8f9fa" : "#fff", width: FIXE_SPALTEN[1].width, overflow: "hidden"}}>
                {canArtikel ? (
                    <Link href={`/cosmos/artikel/${zeile.artikel_nummer}`} className="small">
                        {zeile.artikel_nummer}
                    </Link>
                ) : (
                    <span className="small">{zeile.artikel_nummer}</span>
                )}
            </td>
            <td style={{position: "sticky", left: LINKS[2], zIndex: 2, background: gedaempft ? "#f8f9fa" : "#fff", width: FIXE_SPALTEN[2].width, overflow: "hidden"}}>
                <ZellMitTooltip text={herkunftText(zeile.bestand)}>{formatMenge(zeile.bestand?.wert)}</ZellMitTooltip>
            </td>
            <td style={{position: "sticky", left: LINKS[3], zIndex: 2, background: gedaempft ? "#f8f9fa" : "#fff", width: FIXE_SPALTEN[3].width, overflow: "hidden"}}>
                <ZellMitTooltip text={herkunftText(zeile.offene_kundenmenge)}>{formatMenge(zeile.offene_kundenmenge?.wert)}</ZellMitTooltip>
            </td>
            <td style={{position: "sticky", left: LINKS[4], zIndex: 2, background: gedaempft ? "#f8f9fa" : "#fff", width: FIXE_SPALTEN[4].width, overflow: "hidden"}}>
                {zeile.ausfallwoche?.wert ? (
                    <ZellMitTooltip text={herkunftText(zeile.ausfallwoche)}>{formatKw(zeile.ausfallwoche.wert)}</ZellMitTooltip>
                ) : (
                    <ZellMitTooltip text="Keine Ausfallwoche im Planungshorizont – der Bestand reicht so lange, wie vorausberechnet wird.">–</ZellMitTooltip>
                )}
            </td>
            <td style={{position: "sticky", left: LINKS[5], zIndex: 2, background: gedaempft ? "#f8f9fa" : "#fff", width: FIXE_SPALTEN[5].width, overflow: "hidden"}}>
                <div className="d-flex align-items-center gap-1">
                    {zeile.reichweite_tage?.wert !== null && zeile.reichweite_tage?.wert !== undefined ? (
                        <ZellMitTooltip text={herkunftText(zeile.reichweite_tage)}>{formatMenge(zeile.reichweite_tage.wert)} Tage</ZellMitTooltip>
                    ) : (
                        <ZellMitTooltip text="Keine Reichweite berechenbar – der Bestand reicht länger als der Planungshorizont.">–</ZellMitTooltip>
                    )}
                    {warnung && (
                        <ZellMitTooltip
                            text={`${herkunftText(zeile.warnung_zu_spaet)}${zeile.warnung_zu_spaet?.luecke_wochen ? " · Lücke von " + zeile.warnung_zu_spaet.luecke_wochen + " Wochen" : ""}`}
                        >
                            <PiWarningBold className="text-danger" aria-label="Warnung: zu spät" />
                        </ZellMitTooltip>
                    )}
                </div>
            </td>

            {nichtGerechnet ? (
                <td colSpan={horizontWochen.length} className="text-muted small">
                    {zeile.grund_nicht_gerechnet}
                </td>
            ) : (
                horizontWochen.map((kw) => {
                    const zelle = zeile.bestand_woche?.[kw];
                    const farbe = zellFarbe(zelle?.wert ?? null, mindestbestand, zielbestand, auslaufend);
                    // N10: Zulauf bevorzugt aus dem szenario-eigenen zulauf_woche
                    // (kommt aus derselben Prognose wie die Bestandszahlen selbst) -
                    // Rückfall auf das ältere, zellen-eigene Kennzeichen, solange das
                    // Feld vom Server noch nicht mitgeliefert wird.
                    const zulaufMenge = zeile.zulauf_woche?.[kw];
                    const zulauf = zulaufMenge !== undefined && zulaufMenge !== null ? Number(zulaufMenge) > 0 : Boolean(zelle?.zulauf);

                    return (
                        <td key={kw} style={{width: WOCHE_BREITE, overflow: "hidden", ...farbe}}>
                            <ZellMitTooltip
                                text={zulaufMenge ? `${herkunftText(zelle) || ""}${herkunftText(zelle) ? " · " : ""}Zulauf in dieser Woche: ${formatMenge(zulaufMenge)} Stück` : herkunftText(zelle)}
                                className="d-inline-flex align-items-center gap-1"
                            >
                                {zelle ? formatMenge(zelle.wert) : "–"}
                                {zulauf && <PiArrowUpBold aria-label="Zulauf in dieser Woche" />}
                            </ZellMitTooltip>
                        </td>
                    );
                })
            )}
        </tr>
    );
});

function produktlinienGruppen(zeilen) {
    const gruppen = [];
    let aktuelle = null;
    for (const zeile of zeilen) {
        if (!aktuelle || aktuelle.produktlinie !== zeile.produktlinie) {
            aktuelle = {produktlinie: zeile.produktlinie, zeilen: []};
            gruppen.push(aktuelle);
        }
        aktuelle.zeilen.push(zeile);
    }
    return gruppen;
}

/** Vergleichswert einer Zeile für eine sortierbare Spalte (T8). */
function vergleichswert(zeile, spalte) {
    switch (spalte) {
        case "ident":
            return `${String(zeile.farbnummer ?? 999999).padStart(6, "0")}-${zeile.farbnummer_unter ?? ""}`;
        case "artikelnummer":
            return zeile.artikel_nummer ?? "";
        case "bestand":
            return zeile.bestand?.wert ?? Number.NEGATIVE_INFINITY;
        case "offen":
            return zeile.offene_kundenmenge?.wert ?? Number.NEGATIVE_INFINITY;
        case "ausfallwoche":
            return zeile.ausfallwoche?.wert ?? "9999-99";
        case "reichweite":
            return zeile.reichweite_tage?.wert ?? Number.POSITIVE_INFINITY;
        default:
            return 0;
    }
}

/**
 * Sortiert Zeilen INNERHALB einer Produktlinien-Gruppe (T8: "Gruppierung
 * nach Produktlinie bleibt beim Sortieren erhalten"). "wichtigkeit" ist die
 * Sonderfolge "Wichtigste Farbtöne": von Hand markierte Top-Farbtöne zuerst,
 * danach nach Verkaufsrang bzw. Jahresverbrauch der letzten 52 Wochen -
 * beide Felder sind Rückfälle, solange der Rechenkern sie noch nicht liefert.
 */
/**
 * Auslaufende Farbtöne (Freigabe Thorsten 22.09.2026) stehen in JEDER
 * Sortierung ganz unten - unabhängig von Spalte und Richtung. Gilt als
 * erster Vergleichsschritt vor jedem übrigen Sortierkriterium.
 */
function vergleicheAuslaufend(a, b) {
    const ausA = a.auslaufend ? 1 : 0;
    const ausB = b.auslaufend ? 1 : 0;
    return ausA - ausB;
}

function sortiereZeilen(zeilen, sortSpalte, sortRichtung) {
    if (!sortSpalte) {
        return [...zeilen].sort(vergleicheAuslaufend);
    }
    if (sortSpalte === "wichtigkeit") {
        return [...zeilen].sort((a, b) => {
            const auslaufend = vergleicheAuslaufend(a, b);
            if (auslaufend !== 0) {
                return auslaufend;
            }
            const topA = a.top_farbton ? 1 : 0;
            const topB = b.top_farbton ? 1 : 0;
            if (topA !== topB) {
                return topB - topA;
            }
            const rangA = a.rang_verkauf ?? null;
            const rangB = b.rang_verkauf ?? null;
            if (rangA !== null && rangB !== null) {
                return rangA - rangB;
            }
            const verbrauchA = a.verbrauch_jahr ?? Number.NEGATIVE_INFINITY;
            const verbrauchB = b.verbrauch_jahr ?? Number.NEGATIVE_INFINITY;
            return verbrauchB - verbrauchA;
        });
    }

    const faktor = sortRichtung === "desc" ? -1 : 1;
    return [...zeilen].sort((a, b) => {
        const auslaufend = vergleicheAuslaufend(a, b);
        if (auslaufend !== 0) {
            return auslaufend;
        }
        const wa = vergleichswert(a, sortSpalte);
        const wb = vergleichswert(b, sortSpalte);
        if (wa < wb) {
            return -1 * faktor;
        }
        if (wa > wb) {
            return 1 * faktor;
        }
        return 0;
    });
}

const GESAMT_BREITE_FIX = FIXE_SPALTEN.reduce((summe, s) => summe + s.width, 0);

function SortPfeil({spalte, sortSpalte, sortRichtung}) {
    if (sortSpalte !== spalte) {
        return <PiCaretUpDownBold className="text-muted" aria-hidden="true" />;
    }
    return sortRichtung === "desc" ? <PiCaretDownBold aria-hidden="true" /> : <PiCaretUpBold aria-hidden="true" />;
}

/**
 * Spaltenkopf-Label mit Hilfe-Symbol, hier statt des gemeinsamen
 * HeaderWithHelp.jsx - dessen "align-items-center" ließ das Fragezeichen
 * bei schmalen Spalten (z. B. "Offen, nicht reserviert (Planung)", 150px) mittig über
 * einem zweizeilig umgebrochenen Label sitzen und die zweite Zeile
 * überlappen (Befund Sichtprüfung 3 #S3). "align-items-start" hält das
 * Symbol oben neben der ersten Zeile.
 */
function SpaltenKopfLabel({label, hilfe}) {
    return (
        <span className="d-inline-flex align-items-start gap-1">
            {label}
            {hilfe && (
                <span className="flex-shrink-0" style={{marginTop: 2}}>
                    <HelpTooltip placement="top" size={0.7}>
                        {hilfe}
                    </HelpTooltip>
                </span>
            )}
        </span>
    );
}

/**
 * Die Haupttabelle der Lieferfähigkeit: eine Zeile je Farbton, gruppiert
 * nach Produktlinie (Zwischenüberschrift), Kalenderwochen als Spalten
 * (Auftrag AP3 B). Standardmäßig sind rund 16 Wochenspalten ohne Scrollen
 * sichtbar, der übrige Horizont liegt hinter waagerechtem Scrollen mit
 * fixierten vorderen Spalten (position: sticky). Darüber eine Zeitleiste
 * mit Jahr/Quartal (T6) sowie sortierbare Spaltenköpfe und ein
 * Stern-Schalter je Zeile für "Top-Farbton" (T8).
 *
 * Performance (528 Zeilen × 16+ Spalten): Zeilen sind memoisiert
 * (LieferfaehigkeitZeile), Tooltips laufen über EINEN gemeinsamen
 * Overlay-Baustein (ZellTooltip.jsx) statt einer Instanz je Zelle.
 */
export default function LieferfaehigkeitTabelle({zeilen, horizontWochen, canArtikel, canParameters, onToggleTop, sortSpalte, sortRichtung, onSortWechseln, tabellenRef}) {
    if (!zeilen || zeilen.length === 0) {
        return <p className="text-muted">Keine Farbtöne treffen die aktuelle Filterung.</p>;
    }

    const gruppenRoh = produktlinienGruppen(zeilen);
    const gruppen = gruppenRoh.map((gruppe) => ({
        ...gruppe,
        zeilen: sortiereZeilen(gruppe.zeilen, sortSpalte, sortRichtung),
    }));

    const heutigeWoche = heutigeKw();
    const zeitleisteSpalten = horizontWochen.map((kw) => ({
        schluessel: kw,
        montag: kwZuMontag(kw),
    }));

    return (
        <ZellTooltipProvider>
            <div className="table-responsive border rounded-1" ref={tabellenRef} style={{maxHeight: "70vh"}}>
                <table
                    className="table table-sm table-hover mb-0"
                    style={{minWidth: GESAMT_BREITE_FIX + horizontWochen.length * WOCHE_BREITE, tableLayout: "fixed"}}
                >
                    <colgroup>
                        {FIXE_SPALTEN.map((spalte) => (
                            <col key={spalte.key} style={{width: spalte.width}} />
                        ))}
                        {horizontWochen.map((kw) => (
                            <col key={kw} style={{width: WOCHE_BREITE}} />
                        ))}
                    </colgroup>
                    <thead>
                        <KwZeitleiste spalten={zeitleisteSpalten} vorspaltenAnzahl={FIXE_SPALTEN.length} vorspaltenBreite={GESAMT_BREITE_FIX} topJahr={0} topQuartal={22} />
                        <tr>
                            {FIXE_SPALTEN.map((spalte, index) => (
                                <th
                                    key={spalte.key}
                                    style={{
                                        position: "sticky",
                                        left: LINKS[index],
                                        top: 44,
                                        zIndex: 3,
                                        background: "#fff",
                                        overflow: "hidden",
                                        whiteSpace: "normal",
                                        wordBreak: "normal",
                                        overflowWrap: "normal",
                                        hyphens: "none",
                                        verticalAlign: "bottom",
                                    }}
                                >
                                    {spalte.sortierbar ? (
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-decoration-none text-reset d-inline-flex align-items-center gap-1 text-start"
                                            onClick={() => onSortWechseln?.(spalte.key)}
                                            aria-label={`Nach ${spalte.label} sortieren`}
                                        >
                                            <SpaltenKopfLabel label={spalte.label} hilfe={spalte.hilfe} />
                                            <SortPfeil spalte={spalte.key} sortSpalte={sortSpalte} sortRichtung={sortRichtung} />
                                        </button>
                                    ) : (
                                        <SpaltenKopfLabel label={spalte.label} hilfe={spalte.hilfe} />
                                    )}
                                </th>
                            ))}
                            {horizontWochen.map((kw) => (
                                <th
                                    key={kw}
                                    style={{
                                        position: "sticky",
                                        top: 44,
                                        zIndex: 2,
                                        background: kw === heutigeWoche ? "#e7f1ff" : "#fff",
                                        overflow: "hidden",
                                        verticalAlign: "bottom",
                                    }}
                                    className="text-nowrap"
                                >
                                    <HeaderWithHelp help={`Erwarteter Bestand am Sonntag der ${formatKw(kw)} im gewählten Szenario.`} placement="top">
                                        <span className="d-flex flex-column lh-1">
                                            <span className={kw === heutigeWoche ? "text-primary fw-semibold" : undefined}>{formatKw(kw)}</span>
                                            <span className="small text-muted fw-normal">{kwZuMontag(kw)?.toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit"})}</span>
                                        </span>
                                    </HeaderWithHelp>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {gruppen.map((gruppe) => (
                            <React.Fragment key={gruppe.produktlinie}>
                                <tr>
                                    <td
                                        colSpan={FIXE_SPALTEN.length}
                                        className="fw-semibold small text-uppercase bg-body-secondary"
                                        style={{position: "sticky", left: 0, zIndex: 2}}
                                    >
                                        {PRODUKTLINIE_LABEL[gruppe.produktlinie] || gruppe.produktlinie}
                                    </td>
                                    {/*
                                        T7: eine ZWEITE, eigenständige Zelle für den Bereich der
                                        KW-Spalten statt eines einzigen colSpan über die GESAMTE
                                        Zeile - eine derart breite sticky-Zelle (breiter als der
                                        Tabellen-Container) blieb beim waagerechten Scrollen NICHT
                                        stehen, weil ihr eigener Box-Rand schon über den sichtbaren
                                        Bereich hinausragte. Diese zweite Zelle ist bewusst NICHT
                                        sticky, sie füllt den Rest der Zeile farblich weiter.
                                    */}
                                    <td colSpan={horizontWochen.length} className="bg-body-secondary" aria-hidden="true" />
                                </tr>
                                {gruppe.zeilen.map((zeile) => (
                                    <LieferfaehigkeitZeile
                                        key={zeile.artikel_nummer}
                                        zeile={zeile}
                                        horizontWochen={horizontWochen}
                                        canArtikel={canArtikel}
                                        canParameters={canParameters}
                                        onToggleTop={onToggleTop}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </ZellTooltipProvider>
    );
}
